"use client";

import React, {
	createContext,
	useContext,
	useEffect,
	useCallback,
	useRef,
	useState,
	ReactNode,
} from "react";
import { Hub } from "aws-amplify";
import {
	getMyOnboardedProfile,
	OnboardedProfileResponse,
} from "@/services/onboarding-api";
import {
	getActiveSubscription,
	SubscriptionResponse,
} from "@/services/stripe-api";
import { isAuthenticated } from "@/lib/auth";
import { clearImageryEmailCookie } from "@/lib/cookies";

interface OnboardingContextType {
	profile: OnboardedProfileResponse | null;
	isOnboarded: boolean;
	isLoading: boolean;
	userNotFound: boolean; // True if user account doesn't exist in database
	isError: boolean; // True if there was a network or server error
	errorType: string | null; // The type of error (e.g., "network", "500")
	isSubscribed: boolean;
	subscription: SubscriptionResponse | null;
	refreshProfile: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
	undefined
);

export function OnboardingProvider({ children }: { children: ReactNode }) {
	const [profile, setProfile] = useState<OnboardedProfileResponse | null>(
		null
	);
	const [isLoading, setIsLoading] = useState(true);
	const [userNotFound, setUserNotFound] = useState(false);
	const [isError, setIsError] = useState(false);
	const [errorType, setErrorType] = useState<string | null>(null);
	const [isSubscribed, setIsSubscribed] = useState(false);
	const [subscription, setSubscription] =
		useState<SubscriptionResponse | null>(null);
	const [isOnboarded, setIsOnboarded] = useState(false);

	// Track last fetch time to avoid excessive refetches
	const lastFetchTimeRef = useRef<number>(0);
	const FETCH_COOLDOWN = 2000; // Minimum 2 seconds between refetches

	const resetState = useCallback(() => {
		setProfile(null);
		setIsLoading(false);
		setUserNotFound(false);
		setIsError(false);
		setErrorType(null);
		setIsSubscribed(false);
		setSubscription(null);
		setIsOnboarded(false);
	}, []);

	const fetchProfile = useCallback(async () => {
		// Always set loading to true at the start of fetch
		setIsLoading(true);
		lastFetchTimeRef.current = Date.now();
		try {
			setUserNotFound(false); // Reset error state
			setIsError(false);
			setErrorType(null);
			const authenticated = await isAuthenticated();
			if (!authenticated) {
				// User is not authenticated, reset all state
				resetState();
				return;
			}

			const profileData = await getMyOnboardedProfile();
			console.log("Fetched onboarding profile:", profileData);
			setProfile(profileData);

			// Fetch subscription status independently
			try {
				const subscriptionData = await getActiveSubscription();
				setSubscription(subscriptionData);
				const active =
					subscriptionData?.status === "active" ||
					subscriptionData?.status === "trialing";
				setIsSubscribed(active);
			} catch (subError) {
				console.error("Failed to fetch subscription:", subError);
				// If profile says subscribed (legacy check), maybe trust it?
				// But user says profile has no field about subscription.
				// So we rely on Stripe API.
			}

			// Profile fetch is complete (even if null/empty)
			// null profile means user exists but hasn't completed onboarding yet
		} catch (error: unknown) {
			console.error("Failed to fetch onboarding profile:", error);
			// Check if error is UserNotFoundError
			if (error instanceof Error && error.name === "UserNotFoundError") {
				console.error("User account not found in database");
				setUserNotFound(true);
				setProfile(null);
				setIsError(false);
			} else {
				// Other errors - network or server error
				setProfile(null);
				setUserNotFound(false);
				setIsError(true);

				// Determine error type based on the error
				if (
					error instanceof Error &&
					(error.message.includes("Failed to fetch") ||
						error.message.includes("Network Error") ||
						error.message.includes("ERR_CONNECTION_REFUSED"))
				) {
					setErrorType("server_connection");
				} else {
					setErrorType("500");
				}
			}
		} finally {
			setIsLoading(false);
		}
	}, [resetState]);

	const refreshProfile = async () => {
		setIsLoading(true);
		await fetchProfile();
	};

	useEffect(() => {
		let isMounted = true;

		const initializeProfile = async () => {
			if (isMounted) {
				await fetchProfile();
			}
		};

		initializeProfile();

		// Refresh profile when window gains focus (user might have logged in another tab)
		const handleFocus = async () => {
			if (isMounted) {
				const authenticated = await isAuthenticated();
				if (authenticated) {
					await fetchProfile();
				}
			}
		};

		// window.addEventListener("focus", handleFocus);

		// Listen for auth events to refresh profile when user signs in or reset state when signs out
		const authListener = (data: { payload: { event: string } }) => {
			if (!isMounted) return;

			if (
				data.payload.event === "signIn" ||
				data.payload.event === "cognitoHostedUI"
			) {
				// User just signed in, refresh profile from database
				console.log("Auth event detected, refreshing profile...");
				fetchProfile();
			} else if (data.payload.event === "signOut") {
				// User just signed out, reset all state
				console.log(
					"Sign out event detected, resetting onboarding state..."
				);
				clearImageryEmailCookie();
				resetState();
			}
		};

		Hub.listen("auth", authListener);

		return () => {
			isMounted = false;
			window.removeEventListener("focus", handleFocus);
			Hub.remove("auth", authListener);
		};
	}, [fetchProfile, resetState]);

	// Refetch profile when needed (e.g., when navigating to pages that use useOnboarding)
	// This ensures fresh data when components mount that need onboarding state
	const isLoadingRef = useRef(isLoading);
	useEffect(() => {
		isLoadingRef.current = isLoading;
	}, [isLoading]);

	useEffect(() => {
		const checkAuthAndRefetch = async () => {
			const now = Date.now();
			const timeSinceLastFetch = now - lastFetchTimeRef.current;

			if (timeSinceLastFetch < FETCH_COOLDOWN) {
				return;
			}

			const authenticated = await isAuthenticated();
			if (authenticated && !isLoadingRef.current) {
				lastFetchTimeRef.current = now;
				await fetchProfile();
			}
		};

		const timeoutId = setTimeout(checkAuthAndRefetch, 1000);

		return () => clearTimeout(timeoutId);
	}, [fetchProfile]);

	// Check if onboarding is complete
	// Backend might not have a 'completed' field, so we check if step === 7 (final step)
	// If step is 7, user has completed all onboarding steps
	useEffect(() => {
		if (profile) {
			setIsOnboarded(profile.completed === true || profile.step === 7);
		} else {
			setIsOnboarded(false);
		}
	}, [profile]);
	// Debug logging
	useEffect(() => {
		if (profile) {
			console.log("Onboarding profile state:", {
				hasProfile: !!profile,
				step: profile.step,
				completed: profile.completed,
				isOnboarded,
				selectedStates: profile.selectedStates?.length || 0,
				selectedCounties: profile.selectedCounties?.length || 0,
				referralSource: profile.referralSource,
			});
		} else {
			console.log("No onboarding profile found");
		}
	}, [profile, isOnboarded]);

	return (
		<OnboardingContext.Provider
			value={{
				profile,
				isOnboarded,
				isLoading,
				userNotFound,
				isError,
				errorType,
				isSubscribed,
				subscription,
				refreshProfile,
			}}
		>
			{children}
		</OnboardingContext.Provider>
	);
}

export function useOnboarding() {
	const context = useContext(OnboardingContext);
	if (context === undefined) {
		throw new Error(
			"useOnboarding must be used within an OnboardingProvider"
		);
	}
	return context;
}
